
import { TableRow, TableCell, Collapse, Box, Table, TableBody, SxProps, Theme, MenuItem, useTheme, useMediaQuery } from "@mui/material";
import React, { useState } from "react";
import { IKeyValuePair } from "../../common/models/KeyValuePair";
import ManualEntryFormUtils, { ManualEntryFormMode, ManualEntryFormType, TagFormType } from "./ManualEntryFormUtils";
import { CollapsibleCell } from "../tank-relationships-view-content/common/CollapsibleCell";
import { useFormContext } from "react-hook-form";
import FormSelect from "../forms/form-select/FormSelect";
import { ObjectFeaturesEnum } from "../../common/enums/ObjectFeaturesEnum";
import FormTimeInput from "../forms/form-time-input/FormTimeInput";
import { FormFeetAndInchesPicker } from "../forms/form-feet-and-inches-picker/FormFeetAndInchesPicker";
import { LiquidProductionTATEnum } from "../../common/enums/LiquidProductionTATEnum";
import FormFeetAndInchesMask from "../forms/form-feet-and-inches-mask/FormFeetAndInchesMaskInput";
import FormInput from "../forms/form-input/FormInput";
import { getTimeframeTimeDisplayText } from "../../common/utilities/TimeframeUtilities";
import { TimeframeTypeEnum } from "../../common/enums/TimeframeTypeEnum";

export type DetailRowProps = {
    mode: ManualEntryFormMode,
    item: IKeyValuePair<string, TagFormType[]>,
    tolerance: number,
    viewAllTags: boolean;
    sx?: SxProps<Theme>
};

export function DetailRow({
    mode,
    item,
    tolerance,
    viewAllTags,
    sx
}: DetailRowProps) {

    const [open, setOpen] = useState(true);
    const theme = useTheme();

    const {
        //watch,
        getValues,
        clearErrors,
        formState: {
            errors
        }
    } = useFormContext<ManualEntryFormType>();

    const tags = getValues('tags');    


    const small = useMediaQuery(theme.breakpoints.down("md"));

    function _validateTankLevel(value: number, tag: TagFormType): boolean | string {
        return tag.tatID === LiquidProductionTATEnum.TopLevel ?
            _validateTankTopLevel(value, tag) :
            _validateInterfaceLevelOpen(value, tag);
    }


    function _validateTankTopLevel(value: number, tag: TagFormType): boolean | string {

        const topLevelIndex = tags.findIndex(o => o.id === tag.id);

        const result = ManualEntryFormUtils.validateTankTopLevel(tags, value, tag);

         if (result === true) {
             clearErrors(`tags.${topLevelIndex}.value`);
         }

        return result;

    }

    function _validateInterfaceLevelOpen(value: number, tag: TagFormType): boolean | string {

        const interfaceLevelIndex = tags.findIndex(o => o.id === tag.id);
        const result = ManualEntryFormUtils.validateTankTopLevel(tags, value, tag);

         if (result === true) {
             clearErrors(`tags.${interfaceLevelIndex}.value`);
         }

        return result;
    }

    
    function _validateGasAnalysisInput(tag: TagFormType): boolean | string {

        // sum all gas analysis tag values, the total must not be more than 100% + tolerance.
        const gasAnalysisTags = getValues('tags').filter(o => (o.features & ObjectFeaturesEnum.GasComposition) > 0);

        const result = ManualEntryFormUtils.validateGasAnalysisInput(gasAnalysisTags, tolerance);


        if (result === true) {
            clearErrors('tags');
        }

        return result;
    }

    function renderInputControl(tag: TagFormType) {

        const allowEdit = mode === ManualEntryFormMode.WriteDown ? 
                            (tag.features & ObjectFeaturesEnum.Writedown) > 0 :
                            (tag.features & ObjectFeaturesEnum.ManualEntry) > 0;

        const realIndex = tags.findIndex(o => o.id === tag.id);
        const errorKeys = errors.tags ? Object.keys(errors.tags) : [];
        const hasErrors = errorKeys.find(o => +o === realIndex) !== undefined;

        return (
            <TableRow 
                key={`${tag.id}-${viewAllTags}`}
                sx={{
                    //backgroundColor: "custom.background.2",
                    '& > td': {
                        fontSize: '0.85rem',
                        paddingBottom: '4px',
                        minWidth: '100px',
                        height: hasErrors ? small ? '160px' : '120px' : '48px'
                    },

                    '& > td > div': {
                        marginTop: 'auto'
                    }
                }}
            >


            <TableCell
                sx={{
                    width: '400px',
                }}
            > 
                <Box 
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'inline-flex',
                        

                }}>
                    {tag.name}
                </Box>
            </TableCell>
            <TableCell 
                sx={{
                    width: '300px'
                }}
            
            > 
                <Box sx={{
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'inline-flex'

                }}>
                    {tag.tagValue?.displayText ?? ''}
                </Box>                    
            </TableCell>
            <TableCell
                sx={{
                    width: '75px',
                }}
            > 
                <Box>
                    {tag.unitDisplayName}
                </Box>
            </TableCell>
            <TableCell sx={{width: '200px'}}> 
                <Box>
                    {getTimeframeTimeDisplayText(TimeframeTypeEnum.None, tag.tagValue?.timestamp ?? '')}
                </Box>
            </TableCell>

            <TableCell 
                sx={{
                    paddingTop: '1px',
                    width: '300px',
                    position: 'relative',
                }}
            >
                {
                 !allowEdit ?
                        null :
                    <Box sx={{
                        position: 'absolute',
                        //top: 0,
                        left: '12px',
                        right: 0,
                        bottom: '6px',

                    }}>


                    {tag.lookupList ? 

                    
                    <FormSelect
                        name={`tags.${realIndex}.value`}
                        boldOnDirty={true}
                        sx={{
                            display: 'inline-flex',
                            width: 1,
                            "& .MuiFilledInput-root": {
                                fontSize: '0.85rem',
                                paddingTop: 0
                            },
                            "& .MuiFilledInput-root input": {
                                paddingTop: '12px',
                            }

                        }}
                        // helperText={<Stack display='flex' direction='row' justifyContent='space-between' component='span'>
                        //     <Box component='span'>{tag.unitDisplayName}</Box>
                        //     {mode === ManualEntryFormMode.WriteDown && <Box component='span'>{tag.timestamp}</Box>}
                        // </Stack>}
                        size="small"
                    >
                        <MenuItem value={-1} disabled={mode === ManualEntryFormMode.WriteDown}></MenuItem>
                        {tag.lookupList?.listText.map(o =>
                            <MenuItem key={o.id} value={+(o.value as string)}>{o.description}</MenuItem>
                        )}
                        
                    </FormSelect>
                    
            

                :
            ((tag.features ?? 0) & ObjectFeaturesEnum.HoursMinutesSeconds) > 0 ?
                        <FormTimeInput 
                        
                            name={`tags.${realIndex}.value`}
                            sx={{
                                width: 1,
                                "& .MuiFilledInput-root": {
                                    fontSize: '0.85rem',
                                    paddingTop: 0,
                                },
                                "& .MuiFilledInput-root input": {
                                    paddingTop: '12px',
                                }

                            }}
                            size="small"
                            />
                        :
                       
            ((tag.features ?? 0) & ObjectFeaturesEnum.FeetAndInches) > 0 ?
             tag.tankStyle ?

                    <FormFeetAndInchesPicker
                        name={`tags.${realIndex}.value`}
                        maxFeet={tag.tankStyle ? (tag.tankStyle.heightInInches / 12) : 12}
                        boldOnDirty={true}
                        sx={{
                            width: 1,
                            "& .MuiFilledInput-root": {
                                fontSize: '0.85rem',
                                paddingTop: 0,
                            },
                            "& .MuiFilledInput-root input": {
                                paddingTop: '12px',
                            }
                        }}
                         rules={{
                             validate: (value: string) => _validateTankLevel(+value, tag)
                        }}

                        hideClear={mode === ManualEntryFormMode.WriteDown}
                        size="small"
                    />
                 :
                <FormFeetAndInchesMask
                    name={`tags.${realIndex}.feetAndInchesText`}
                    sx={{
                        width: "100%",
                        "& .MuiFilledInput-root": {
                            fontSize: '0.85rem',
                            alignItems: "baseline !important",
                            paddingTop: 0,
                        },
                        "& .MuiFilledInput-root input": {
                            paddingTop: '12px',
                        },


                    }}
                    rules={{
                        validate: (value: string) => {
                            const result = ManualEntryFormUtils.validateFeetAndInches(value, tag);

                            if (result === true) {
                                clearErrors(`tags.${realIndex}.feetAndInchesText`);
                            }
                            return result;
                        }
                    }}
                    size='small'
                />

                 :
                 <FormInput
                    sx={{
                        width: 1,
                        "& .MuiFilledInput-root": {
                            fontSize: '0.85rem',
                            paddingTop: 0,
                        },
                        "& .MuiFilledInput-root input": {
                            paddingTop: '12px',
                        },
                    }}
                    name={`tags.${realIndex}.value`}
                    numeric={true}
                    decimals={mode === ManualEntryFormMode.WriteDown ? 99 : tag.decimalPlaces}
                    boldOnDirty={true}
                    color ={(tag.features & ObjectFeaturesEnum.GasComposition) > 0 ? '#006400' : undefined}
                    rules={{
                        validate: (value: number | null) => mode === ManualEntryFormMode.WriteDown && (tag.features & ObjectFeaturesEnum.GasComposition) > 0 ? 
                                        _validateGasAnalysisInput(tag) : 
                                        ManualEntryFormUtils.validateBounds(mode, value === null ? undefined : +value, tag)
                    }}
                    size='small'
                />
                }           
                
                </Box>
                    
                }
            </TableCell>

            </TableRow>
        )
    }


    return (

        <React.Fragment>
            <TableRow sx={{ 
                backgroundColor: 'custom.background.1',
                    '& > *': { 
                        borderBottom: 'unset',
                        padding: 0
                    },
                }}
            >
                <CollapsibleCell text={item.Key} open={open} onClick={() => setOpen(!open)}  
                    sx={sx}
                    colSpan={5}
                />

            </TableRow>
            <TableRow>
                <TableCell sx={{ 
                    paddingBottom: 0, 
                    paddingTop: 0,
                    paddingLeft: 1,
                    paddingRight: 1,
                    margin: 2,
                    }} colSpan={5}
                >

                <Collapse in={open} timeout="auto" unmountOnExit>

                    <Box sx={{ margin: 1 }}>

                            <Table size="small">

                            <TableBody>
                                
                                <>
                                {item.Value.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((v) => (
                                    renderInputControl(v)
                                ))}

                                </>
                                
                            </TableBody>
                        </Table>
                    </Box>
                </Collapse>
            </TableCell>
            </TableRow>
        </React.Fragment>
    )

    
}
